import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Chip,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import PersonIcon from "@mui/icons-material/Person";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import UserEditDialog from "../components/UserEditDialog";
import UserAddDialog from "../components/UserAddDialog";
import FormatListBulletedAddIcon from "@mui/icons-material/FormatListBulletedAdd";
import TableRowsIcon from "@mui/icons-material/TableRows";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import UserCards from "../components/UserCards";
import UserTable from "../components/UserTable";
import type { UserGroupSummary, UserSummary } from "../types/user";

type UserSummaryResponse = {
  id: string;
  username: string;
  email: string;
  name?: string;
  surname?: string;
  company?: string;
  country?: string;
  role: string;
  active: boolean;
  userGroups?: string[];
  createdAt?: string | null;
  lastSeen?: string | null;
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const createDefaultSnackbarState = (): SnackbarState => ({
  open: false,
  message: "",
  severity: "success"
});

type UsersViewMode = "Cards" | "Table";
const USERS_VIEW_MODE_STORAGE_KEY = "usersPageViewMode";

const UsersPage = () => {
  const { authorizedFetch, user: authUser, resolveApiPath } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const queryClient = useQueryClient();
  const [usersViewMode, setUsersViewMode] = useState<UsersViewMode>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(USERS_VIEW_MODE_STORAGE_KEY);
      if (stored === "Cards" || stored === "Table") {
        return stored;
      }
    }
    return "Cards";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(USERS_VIEW_MODE_STORAGE_KEY, usersViewMode);
    } catch (err) {
      console.error("Failed to persist users view mode", err);
    }
  }, [usersViewMode]);

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<UserSummary[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/users");
      const payload = (await response.json().catch(() => null)) as {
        users?: UserSummaryResponse[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load users");
      }
      return (payload?.users ?? []).map((user) => ({
        ...user,
        name: user.name ?? "",
        surname: user.surname ?? "",
        company: user.company ?? "",
        country: typeof user.country === "string" ? user.country : "",
        userGroups: user.userGroups ?? [],
        createdAt:
          typeof user.createdAt === "string" && user.createdAt.trim() !== "" ? user.createdAt : null,
        lastSeen:
          typeof user.lastSeen === "string" && user.lastSeen.trim() !== "" ? user.lastSeen : null
      }));
    }
  });

  const users = useMemo(() => data ?? [], [data]);
  const userIdentityList = useMemo(
    () => users.map(({ id, username, email }) => ({ id, username, email })),
    [users]
  );

  const {
    data: userGroupsData,
    isLoading: isLoadingUserGroups,
    isError: isUserGroupsError,
    error: userGroupsError
  } = useQuery<UserGroupSummary[]>({
    queryKey: ["usergroups"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/usergroups");
      const payload = (await response.json().catch(() => null)) as {
        userGroups?: UserGroupSummary[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load user groups");
      }
      return payload?.userGroups ?? [];
    },
    enabled: isAdmin
  });

  const availableUserGroups = useMemo(() => userGroupsData ?? [], [userGroupsData]);
  const userGroupMap = useMemo(() => {
    const entries = availableUserGroups.map((group) => [group.id, group.name] as const);
    return new Map(entries);
  }, [availableUserGroups]);

  const userById = useMemo(() => {
    const map = new Map<string, UserSummary>();
    for (const user of users) {
      map.set(user.id, user);
    }
    return map;
  }, [users]);

  const [passwordUser, setPasswordUser] = useState<UserSummary | null>(null);
  const [editUser, setEditUser] = useState<UserSummary | null>(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [deleteTargets, setDeleteTargets] = useState<UserSummary[]>([]);
  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>(createDefaultSnackbarState);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const selectedUsersForExport = useMemo(
    () =>
      selectedUserIds
        .map((id) => userById.get(id))
        .filter((value): value is UserSummary => Boolean(value)),
    [selectedUserIds, userById]
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const closeSnackbar = () => {
    setSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
  };

  const invalidateUsers = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  }, [queryClient]);

  const handleViewModeChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: UsersViewMode | null) => {
      if (!value) {
        return;
      }
      if (value !== "Table") {
        setSelectedUserIds([]);
      }
      setUsersViewMode(value);
    },
    []
  );

  const updateMutation = useMutation<
    void,
    Error,
    {
      id: string;
      role: string;
      active: boolean;
      userGroups: string[];
      username: string;
      email: string;
      name: string;
      surname: string;
      company: string;
      country: string;
    },
    { previousUsers?: UserSummary[] }
  >({
    mutationFn: async ({ id, role, active, userGroups, username, email, name, surname, company, country }) => {
      const response = await authorizedFetch(`/api/users/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, name, surname, company, country, role, active, userGroups })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to update user";
        throw new Error(message);
      }
    },
    onMutate: async ({ id, role, active, userGroups, username, email, name, surname, company, country }) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousUsers = queryClient.getQueryData<UserSummary[]>(["users"]);
      if (previousUsers) {
        const nextUsers = previousUsers.map((existing) =>
          existing.id === id
            ? { ...existing, role, active, userGroups, username, email, name, surname, company, country }
            : existing
        );
        queryClient.setQueryData(["users"], nextUsers);
      }

      return { previousUsers };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(["users"], context.previousUsers);
      }
    },
    onSettled: () => {
      invalidateUsers();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authorizedFetch(`/api/users/${id}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to delete user";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidateUsers();
    }
  });

  const handleExportUsers = useCallback(() => {
    if (!isAdmin || selectedUsersForExport.length === 0) {
      return;
    }
    setIsExporting(true);
    try {
      const sanitizedUsers = selectedUsersForExport.map((user) => ({
        username: user.username.trim(),
        email: user.email.trim(),
        name: user.name.trim(),
        surname: user.surname.trim(),
        company: user.company.trim(),
        country: user.country.trim(),
        active: Boolean(user.active)
      }));

      const payload = { users: sanitizedUsers };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = url;
      link.download = `users-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: "Users exported.", severity: "success" });
    } catch (err) {
      console.error("Export users failed", err);
      setSnackbar({ open: true, message: "Failed to export users.", severity: "error" });
    } finally {
      setIsExporting(false);
    }
  }, [isAdmin, selectedUsersForExport]);

  const handleImportFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!isAdmin) {
        return;
      }
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) {
        return;
      }
      setIsImporting(true);
      try {
        const formData = new FormData();
        formData.append("file", file, file.name);

        const response = await authorizedFetch("/api/users/import", {
          method: "POST",
          body: formData
        });
        const payload = (await response.json().catch(() => null)) as {
          importedUsers?: number;
          error?: string;
          errors?: string[];
          skippedUsernames?: string[];
          skippedEmails?: string[];
        } | null;
        if (!response.ok) {
          const message = payload?.error ?? (payload?.errors?.[0] ?? "Failed to import users.");
          throw new Error(message);
        }

        const importedUsers = Number(payload?.importedUsers ?? 0);
        const skippedUsernames = Array.isArray(payload?.skippedUsernames) ? payload?.skippedUsernames : [];
        const skippedEmails = Array.isArray(payload?.skippedEmails) ? payload?.skippedEmails : [];
        const errorDetails = Array.isArray(payload?.errors) ? payload?.errors : [];
        const hasErrors = errorDetails.length > 0;

        const parts = [`Users: ${importedUsers}`];
        if (skippedUsernames.length > 0 || skippedEmails.length > 0) {
          parts.push(
            `Skipped (${skippedUsernames.length} usernames, ${skippedEmails.length} emails)`
          );
        }
        if (hasErrors) {
          parts.push(`Errors: ${errorDetails.slice(0, 3).join("; ")}`);
        }

        setSnackbar({
          open: true,
          message: `Import complete. ${parts.join(". ")}`,
          severity: hasErrors ? "error" : "success"
        });
        setSelectedUserIds([]);
        await invalidateUsers();
      } catch (err) {
        console.error("Import users failed", err);
        const message = err instanceof Error ? err.message : "Failed to import users.";
        setSnackbar({ open: true, message, severity: "error" });
      } finally {
        setIsImporting(false);
      }
    },
    [authorizedFetch, invalidateUsers, isAdmin]
  );

  const handleImportClick = useCallback(() => {
    if (!isAdmin) {
      return;
    }
    fileInputRef.current?.click();
  }, [isAdmin]);

  const handleOpenPassword = (user: UserSummary) => {
    setPasswordUser(user);
    setPasswordValue("");
    setDialogError(null);
    setShowPassword(false);
  };

  const handleClosePassword = () => {
    setPasswordUser(null);
    setPasswordValue("");
    setDialogError(null);
    setSavingPassword(false);
    setShowPassword(false);
  };

  const handleOpenEdit = (user: UserSummary) => {
    setEditUser(user);
  };

  const handleCloseEdit = () => {
    setEditUser(null);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordUser) return;
    if (passwordValue.trim().length < 8) {
      setDialogError("Password must be at least 8 characters.");
      return;
    }
    setSavingPassword(true);
    setDialogError(null);
    try {
      const response = await authorizedFetch(`/api/users/${passwordUser.id}/password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: passwordValue })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to update password";
        setDialogError(message);
        return;
      }

      setSnackbar({ open: true, message: `Password updated for ${passwordUser.username}.`, severity: "success" });
      handleClosePassword();
    } catch (err) {
      console.error(err);
      setDialogError("Unable to update password. Please try again.");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<string>, user: UserSummary) => {
    const nextRole = event.target.value as string;
    if (!nextRole || nextRole === user.role) {
      return;
    }

    setUpdatingUserId(user.id);
    closeSnackbar();

    updateMutation.mutate(
      {
        id: user.id,
        role: nextRole,
        active: user.active,
        userGroups: user.userGroups ?? [],
        username: user.username,
        email: user.email,
        name: user.name,
        surname: user.surname,
        company: user.company,
        country: user.country
      },
      {
        onSuccess: () => {
          setSnackbar({ open: true, message: `${user.username} is now ${nextRole}.`, severity: "success" });
        },
        onError: (err) => {
          console.error(err);
          const message = err instanceof Error ? err.message : "Unable to update user role.";
          setSnackbar({ open: true, message, severity: "error" });
        },
        onSettled: () => {
          setUpdatingUserId(null);
        }
      }
    );
  };

  const handleUserGroupsChange = (event: SelectChangeEvent<string[]>, user: UserSummary) => {
    if (!isAdmin || user.role === "admin") {
      return;
    }

    const value = event.target.value;
    const rawGroups = typeof value === "string" ? value.split(",") : value;
    const normalized = Array.from(
      new Set(
        rawGroups
          .map((group) => group.trim())
          .filter((group) => group.length > 0)
      )
    );

    const currentGroups = user.userGroups ?? [];
    const currentSet = new Set(currentGroups);
    const changed =
      normalized.length !== currentGroups.length || normalized.some((group) => !currentSet.has(group));
    if (!changed) {
      return;
    }

    setUpdatingUserId(user.id);
    closeSnackbar();

    updateMutation.mutate(
      {
        id: user.id,
        role: user.role,
        active: user.active,
        userGroups: normalized,
        username: user.username,
        email: user.email,
        name: user.name,
        surname: user.surname,
        company: user.company,
        country: user.country
      },
      {
        onSuccess: () => {
          setSnackbar({ open: true, message: `${user.username} groups updated.`, severity: "success" });
        },
        onError: (err) => {
          console.error(err);
          const message = err instanceof Error ? err.message : "Unable to update user groups.";
          setSnackbar({ open: true, message, severity: "error" });
        },
        onSettled: () => {
          setUpdatingUserId(null);
        }
      }
    );
  };

  const handleOpenDelete = (user: UserSummary | UserSummary[]) => {
    const targets = Array.isArray(user) ? user : [user];
    if (!targets.length) return;
    setDeleteTargets(targets);
    setDeleteError(null);
  };

  const handleCloseDelete = () => {
    setDeleteTargets([]);
    setDeleteError(null);
    setDeletingUser(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargets.length) return;
    setDeletingUser(true);
    setDeleteError(null);
    try {
      for (const target of deleteTargets) {
        // eslint-disable-next-line no-await-in-loop
        await deleteMutation.mutateAsync(target.id);
      }
      setSnackbar({
        open: true,
        message:
          deleteTargets.length === 1
            ? `${deleteTargets[0].username} was removed.`
            : `${deleteTargets.length} users were removed.`,
        severity: "success"
      });
      setSelectedUserIds((prev) => prev.filter((id) => !deleteTargets.some((target) => target.id === id)));
      handleCloseDelete();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Unable to delete user.";
      setDeleteError(message);
    } finally {
      setDeletingUser(false);
    }
  };

  const userGroupsErrorMessage = useMemo(() => {
    if (!isUserGroupsError) {
      return null;
    }
    return userGroupsError instanceof Error ? userGroupsError.message : "Unable to load user groups.";
  }, [isUserGroupsError, userGroupsError]);

  const deletingUserId = deleteTargets.length === 1 && deletingUser ? deleteTargets[0].id : null;

  const viewToggleButtons = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={usersViewMode}
      onChange={handleViewModeChange}
      aria-label="Users view style"
      color="primary"
      sx={{ ml: { xs: 0, sm: 0.75 } }}
    >
      <ToggleButton value="Cards" aria-label="Cards view">
        <Tooltip title="Cards" describeChild>
          <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DashboardCustomizeIcon fontSize="small" />
          </Box>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="Table" aria-label="Table view">
        <Tooltip title="Table" describeChild>
          <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TableRowsIcon fontSize="small" />
          </Box>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const actionButtons = (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      <Button
        variant="outlined"
        startIcon={<FormatListBulletedAddIcon />}
        onClick={() => setAddUserOpen(true)}
        disabled={!isAdmin}
        sx={{ whiteSpace: "nowrap" }}
      >
        Add user
      </Button>
      {usersViewMode === "Table" ? (
        <>
          <Tooltip title="Edit user">
            <span>
              <IconButton
                color="primary"
                aria-label="Edit user"
                onClick={() => {
                  const id = selectedUserIds[selectedUserIds.length - 1];
                  if (!id) return;
                  const target = userById.get(id);
                  if (target) {
                    handleOpenEdit(target);
                  }
                }}
                disabled={!isAdmin || selectedUserIds.length !== 1}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete user(s)">
            <span>
              <IconButton
                color="primary"
                aria-label="Delete user"
                onClick={() => {
                  const targets = selectedUserIds
                    .map((id) => userById.get(id))
                    .filter((value): value is UserSummary => Boolean(value));
                  if (targets.length) {
                    handleOpenDelete(targets);
                  }
                }}
                disabled={!isAdmin || selectedUserIds.length === 0}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : null}
      <Tooltip title="Export users">
        <span>
          <IconButton
            color="primary"
            aria-label="Export users"
            onClick={handleExportUsers}
            disabled={!isAdmin || isExporting || selectedUsersForExport.length === 0}
            size="small"
          >
            <FileDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Import users">
        <span>
          <IconButton
            color="primary"
            aria-label="Import users"
            onClick={handleImportClick}
            disabled={!isAdmin || isImporting}
            size="small"
          >
            <FileUploadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const tableToolbarActions = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="flex-end"
      sx={{ width: { xs: "100%", sm: "auto" } }}
    >
      {viewToggleButtons}
      {actionButtons}
    </Stack>
  );

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Box display="inline-flex" alignItems="center" gap={1}>
            <PersonIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h5" sx={{ m: 0 }}>
              Users
            </Typography>
            <Badge badgeContent={users.length} color="primary" sx={{ ml: 1 }} />
          </Box>
        </Box>
        {usersViewMode === "Table" ? null : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {viewToggleButtons}
            {actionButtons}
          </Stack>
        )}
      </Box>
      <Stack spacing={2}>
        {isAdmin && isUserGroupsError ? (
          <Alert severity="error">{userGroupsErrorMessage ?? "Unable to load user groups."}</Alert>
        ) : null}
        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Alert severity="error">{error instanceof Error ? error.message : "Unable to load users."}</Alert>
        ) : users.length === 0 ? (
          <Alert severity="info">No users found.</Alert>
        ) : usersViewMode === "Cards" ? (
          <UserCards
            users={users}
            isAdmin={isAdmin}
            availableUserGroups={availableUserGroups}
            userGroupMap={userGroupMap}
            isLoadingUserGroups={isLoadingUserGroups}
            isUserGroupsError={isUserGroupsError}
            userGroupsErrorMessage={userGroupsErrorMessage}
            updatingUserId={updatingUserId}
            deletingUserId={deletingUserId}
            onRoleChange={handleRoleChange}
            onUserGroupsChange={handleUserGroupsChange}
            onResetPassword={handleOpenPassword}
            onEdit={handleOpenEdit}
            onDelete={handleOpenDelete}
          />
        ) : (
          <UserTable
            users={users}
            selectedUserIds={selectedUserIds}
            onSelectionChange={setSelectedUserIds}
            toolbarActions={usersViewMode === "Table" ? tableToolbarActions : undefined}
          />
        )}
      </Stack>
      <Dialog open={Boolean(passwordUser)} onClose={handleClosePassword} fullWidth maxWidth="sm">
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter a new password for {passwordUser?.username}. Passwords must be at least eight characters long.
          </Typography>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <TextField
            fullWidth
            label="New password"
            type={showPassword ? "text" : "password"}
            value={passwordValue}
            onChange={(event) => setPasswordValue(event.target.value)}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePassword} disabled={savingPassword}>
            Cancel
          </Button>
          <Button onClick={handlePasswordSubmit} variant="contained" disabled={savingPassword}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <UserAddDialog
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        resolveApiPath={resolveApiPath}
        existingUsers={userIdentityList}
        availableUserGroups={availableUserGroups}
        onSuccess={({ message }) => {
          setSnackbar({ open: true, message, severity: "success" });
          setAddUserOpen(false);
          invalidateUsers();
        }}
      />
      <UserEditDialog
        open={Boolean(editUser)}
        user={
          editUser
            ? {
                ...editUser,
                userGroups: editUser.userGroups ?? []
              }
            : null
        }
        existingUsers={userIdentityList}
        availableUserGroups={availableUserGroups}
        authorizedFetch={authorizedFetch}
        currentUserId={authUser?.id ?? null}
        onClose={handleCloseEdit}
        onResetPassword={(user) => {
          const fullUser = userById.get(user.id);
          if (fullUser) {
            handleOpenPassword(fullUser);
          }
        }}
        onSuccess={({ message, user: updated }) => {
          setSnackbar({ open: true, message, severity: "success" });
          handleCloseEdit();
          queryClient.setQueryData<UserSummary[]>(["users"], (previous) => {
            if (!previous) {
                  return previous;
            }
            return previous.map((candidate) =>
              candidate.id === updated.id
                ? {
                    ...candidate,
                    username: updated.username,
                    email: updated.email,
                    name: updated.name,
                    surname: updated.surname,
                    company: updated.company,
                    country: updated.country,
                    role: updated.role,
                    active: updated.active,
                    userGroups: updated.userGroups ?? []
                  }
                : candidate
            );
          });
          invalidateUsers();
        }}
      />
      <Dialog open={deleteTargets.length > 0} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle>Delete user{deleteTargets.length > 1 ? "s" : ""}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {deleteTargets.length === 1
              ? `Deleting ${deleteTargets[0]?.username} will remove their access to SkyView. This action cannot be undone.`
              : `Deleting ${deleteTargets.length} users will remove their access to SkyView. This action cannot be undone.`}
          </Typography>
          {deleteTargets.length > 1 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {deleteTargets.map((user) => (
                <Chip key={user.id} label={user.username} size="small" variant="outlined" />
              ))}
            </Box>
          ) : null}
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deletingUser}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deletingUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={(_event, reason) => {
          if (reason === "clickaway") {
            return;
          }
          closeSnackbar();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionProps={{
          onExited: () => {
            setSnackbar((prev) => (prev.open ? prev : createDefaultSnackbarState()));
          }
        }}
      >
        {snackbar.message
          ? (
              <Alert
                onClose={() => closeSnackbar()}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            )
          : undefined}
      </Snackbar>
      <input
        type="file"
        ref={fileInputRef}
        accept="application/json"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />
    </Box>
  );
};

export default UsersPage;
